/**
 * Help Page - Documentation and guide for the Ops / Integrations UI.
 *
 * Diaper / Sessions / Clip Simulator content lives in the Diaper UI now
 * (see Diaper UI's own Help page).
 */

import React from 'react';

export const HelpPage: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Nova Control Center - Integrations Ops</h1>
      <p className="text-gray-600 mb-8">
        Operate the Nova → partner event pipeline: configure who sends what, watch it flow, and audit
        what happened.
      </p>

      {/* What is this app */}
      <section className="mb-8 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-primary mb-4">What this UI is for</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          The Ops UI is one of three apps that make up the Nova Integration platform:
        </p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
          <li>
            <strong>Ops UI</strong> (this app) - configure source systems, accounts, tenants and
            partners; watch live integration traffic; inspect audit logs.
          </li>
          <li>
            <strong>Diaper / Sessions UI</strong> - clip simulator, sessions, diaper search /
            inspector, diaper models. Open via the app switcher in the top bar.
          </li>
          <li>
            <strong>Identity Admin</strong> - users, grants and roles. All three apps share one
            login through the identity service.
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed mt-3">
          You log in once at Identity and a JWT is handed to whichever app you open. Use the app
          switcher (top right) to move between apps without re-authenticating.
        </p>
      </section>

      {/* Sidebar tour */}
      <section className="mb-8 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-primary mb-4">Sidebar tour</h2>

        <h3 className="text-xl font-semibold mb-2">Partner</h3>
        <p className="text-gray-700 mb-2">
          Surfaces scoped to a single partner identity. Visible to users with partner capabilities.
        </p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-5">
          <li>
            <strong>My Partner</strong> - the partner's own configuration and delivery summary.
          </li>
          <li>
            <strong>Test Events</strong> - hand-crafted events the partner can fire into their own
            integration to validate retry, transform and delivery behaviour.
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Integration Operations</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-5">
          <li>
            <strong>Dashboard</strong> - top-level health: throughput, recent events, integration
            summary across all partners.
          </li>
          <li>
            <strong>Live Monitor</strong> - real-time stream of every event flowing through the
            platform. Useful while debugging a delivery issue.
          </li>
          <li>
            <strong>Mock PARTNER Monitor</strong> - shows what the built-in mock partner endpoint
            received. Perfect for end-to-end smoke tests without involving an external partner.
          </li>
          <li>
            <strong>Configuration</strong> - landing page for the four config domains below.
          </li>
          <li>
            <strong>RabbitMQ</strong> - queue depth and message inspection for the broker that
            backs delivery.
          </li>
          <li>
            <strong>Audit Log</strong> - who changed what and when, across all configuration and
            user-management actions.
          </li>
          <li>
            <strong>Users</strong> - user listing for the Ops UI. Full grant management lives in
            the Identity Admin app (top-bar switcher).
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Config Management</h3>
        <p className="text-gray-700 mb-2">The "service owns its data" model splits config into four domains:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
          <li>
            <strong>Source Systems</strong> - upstream event sources (Nova, Aquatime, etc.) and the
            shape of their credentials.
          </li>
          <li>
            <strong>Nova Accounts</strong> - credentials and connection details for talking to a
            specific Nova environment (validation / pilot / prod).
          </li>
          <li>
            <strong>Tenants</strong> - logical grouping of sites under a customer. Tenants are how
            partner deliveries are scoped.
          </li>
          <li>
            <strong>Partners</strong> - downstream consumers of the event stream. Per partner you
            configure delivery method, auth, retry policy, alert cycling, and which tenants /
            sites they see.
          </li>
        </ul>
      </section>

      {/* Common workflows */}
      <section className="mb-8 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-primary mb-4">Common workflows</h2>

        <div className="space-y-5">
          <div className="bg-gray-50 rounded-lg p-5">
            <h3 className="text-xl font-semibold mb-2">Onboard a new partner</h3>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
              <li>
                Make sure the right <strong>Source System</strong> and <strong>Nova Account</strong>{' '}
                exist for their environment.
              </li>
              <li>
                Create the <strong>Tenant</strong> (or reuse an existing one) and confirm the site
                tree under it is correct.
              </li>
              <li>
                Create the <strong>Partner</strong> in Configuration → Partners. Pick delivery
                method, auth, retry / cycling policy, and assign the tenants the partner is allowed
                to receive events for.
              </li>
              <li>
                In <strong>Identity Admin</strong>, grant the partner's login user the
                <code className="bg-gray-200 px-1 rounded mx-1">partner.*</code> capabilities and
                pick the partner from the dropdown so they only see their own data.
              </li>
              <li>
                Smoke-test from the partner's own login using <strong>Test Events</strong>, then
                watch <strong>Live Monitor</strong> and the partner's own delivery monitor.
              </li>
            </ol>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h3 className="text-xl font-semibold mb-2">Debug "events aren't reaching partner X"</h3>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
              <li>
                Open <strong>Live Monitor</strong> - confirm the source events are being ingested
                at all.
              </li>
              <li>
                Open <strong>RabbitMQ</strong> - confirm the partner's queue isn't backed up or
                dead-lettered.
              </li>
              <li>
                Open <strong>Configuration → Partners → (the partner)</strong> - verify delivery
                URL, credentials, tenant scope, and retry policy.
              </li>
              <li>
                Use <strong>Mock PARTNER Monitor</strong> as a known-good comparison: re-send a
                test event to the mock and confirm the platform itself is healthy.
              </li>
              <li>
                Check the <strong>Audit Log</strong> for any recent config changes that line up
                with when the issue started.
              </li>
            </ol>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h3 className="text-xl font-semibold mb-2">Generate a test event without a real clip</h3>
            <p className="text-gray-700 mb-2">Two paths, depending on what you want to validate:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>
                <strong>From a partner perspective</strong> - log in as the partner and use the{' '}
                <strong>Test Events</strong> page. Lets you craft and fire an event into your own
                delivery pipeline.
              </li>
              <li>
                <strong>From a clip / device perspective</strong> - jump to the{' '}
                <strong>Diaper / Sessions UI</strong> via the app switcher and use{' '}
                <strong>Clip Simulator</strong> on a session there. The resulting events flow back
                through this Ops UI's Live Monitor.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Capabilities & access */}
      <section className="mb-8 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-primary mb-4">Capabilities &amp; access</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          Sidebar items are filtered by capabilities on your JWT. If a section doesn't appear, you
          probably don't have a grant for it. Common capabilities you'll see referenced:
        </p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
          <li>
            <code className="bg-gray-100 px-1 rounded">ops.events.read.all</code> - Live Monitor,
            Mock PARTNER Monitor.
          </li>
          <li>
            <code className="bg-gray-100 px-1 rounded">ops.queue.read</code> - RabbitMQ.
          </li>
          <li>
            <code className="bg-gray-100 px-1 rounded">ops.tenants.assign</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">ops.partners.read.all</code> - Tenants and
            Partners pages.
          </li>
          <li>
            <code className="bg-gray-100 px-1 rounded">admin.sources.crud</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">admin.accounts.crud</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">admin.partners.crud</code> - editing the
            corresponding configuration domains.
          </li>
          <li>
            <code className="bg-gray-100 px-1 rounded">admin.audit.read</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">admin.users.crud</code> - Audit Log and
            Users.
          </li>
          <li>
            <code className="bg-gray-100 px-1 rounded">partner.config.read.own</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">partner.testevents.send.own</code> - the
            partner-scoped surfaces.
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed mt-3">
          Grants are issued in <strong>Identity Admin</strong>. A user that should "be" a specific
          partner needs both the partner capabilities <em>and</em> a partner selection in their
          grant.
        </p>
      </section>

      {/* Troubleshooting */}
      <section className="mb-8 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-primary mb-4">Troubleshooting</h2>

        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Sidebar items are missing</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>You're missing the capability for that item - check your grants in Identity Admin.</li>
              <li>Log out and back in to refresh the JWT after a grant change.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">App switcher sends me back to login</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>Your JWT expired - that's expected, log in again at Identity.</li>
              <li>
                If it happens immediately on every switch, the target app couldn't validate the
                JWT - check that all three apps point at the same Identity (same JWKS URL).
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">RabbitMQ page is empty</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>Broker may be down - check the docker compose stack.</li>
              <li>You may not have <code className="bg-gray-100 px-1 rounded">ops.queue.read</code>.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Configuration changes "don't take effect"</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>Re-check Audit Log to confirm the save actually happened and as which user.</li>
              <li>
                Some changes (credentials, delivery endpoints) take effect on the next event - send
                a Test Event to verify rather than waiting.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="mt-8 pt-6 border-t border-gray-200">
        <h2 className="text-xl font-bold text-primary mb-3">Support</h2>
        <p className="text-gray-700 leading-relaxed">
          <strong>For Technical Partners:</strong> Contact Abena's commercial organisation to
          initiate event-stream integration setup. We'll work with you to configure delivery
          methods, authentication, retry policies and alert cycling tailored to your platform.
        </p>
        <p className="text-gray-700 leading-relaxed mt-3">
          <strong>For platform issues:</strong> Contact the integrations team. When reporting an
          issue, include the partner ID, an approximate timestamp, and a screenshot of Live Monitor
          / RabbitMQ if available.
        </p>
      </section>
    </div>
  );
};
